// components/organization-switcher.tsx
"use client"

// app/components/organization-switcher.tsx
import * as React from "react"
import { useContext, useState } from "react"
import { OrganizationContext } from "@/contexts/organization"
import { useRoles } from "@/contexts/roles"
import { Check, ChevronsUpDown, PlusCircle } from "lucide-react"
import { useSession } from "next-auth/react"
import { FormattedMessage, useIntl } from "react-intl"

import { cn } from "@/lib/utils"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"

import { toast } from "./ui/use-toast"

type Organization = {
  id: string
  name: string
  subscription_plan: string
  subscription_active: boolean
  subscription_billing_cycle: string
  subscription_seats: number
  ff_public_share: boolean
}

type PopoverTriggerProps = React.ComponentPropsWithoutRef<typeof PopoverTrigger>

interface OrganizationProps extends PopoverTriggerProps {}

export default function Organization({ className }: OrganizationProps) {
  const { isAppOwner } = useRoles()
  const selectedOrganizationRef = React.useRef<Organization | null>(null)
  const intl = useIntl()
  const [open, setOpen] = React.useState(false)
  const [showNewOrganizationDialog, setShowNewOrganizationDialog] =
    React.useState(false)
  const {
    selectedOrganization,
    setSelectedOrganization,
    reloadOrganization,
    setReloadOrganization,
  } = useContext(OrganizationContext)
  const [groups, setGroups] = React.useState<
    { name: string; organizations: Organization[] }[]
  >([])
  const [organizations, setOrganizations] = React.useState<Organization[]>([])
  const [loading, setLoading] = useState(true)
  const [inputName, setInputName] = useState<string>("")
  const [inputEmail, setInputEmail] = useState<string>("")
  const [inputOpenaiKey, setInputOpenaiKey] = useState<string>("")
  const [inputStorageLimit, setInputStorageLimit] = useState<number>(1)
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const { data: session, update } = useSession()

  // Create a ref to store the current selected organization

  const handleSelectOrganization = async (
    organization: Organization,
    force = false
  ) => {
    // Check if the selected organization has changed before updating
    if (selectedOrganizationRef.current?.id !== organization.id || force) {
      // Update the ref with the new selected organization immediately
      selectedOrganizationRef.current = organization

      const updatedSession = { ...session }
      updatedSession.organizationId = organization.id
      updatedSession.organization = organization
      await update(updatedSession)
      setSelectedOrganization(organization)
      window.localStorage.setItem(
        "selectedOrganization",
        JSON.stringify(organization)
      )
    }
    setLoading(false)
  }

  const fetchOrganizationsFromApi = async (force = false) => {
    const res = await fetch(`/api/organizations`)
    const data = await res.json()
    if (res.ok) {
      const organizations = data.map((org: any) => ({
        name: org.name,
        id: org.id,
        subscription_plan: org.subscription_plan,
        subscription_billing_cycle: org.subscription_billing_cycle,
        subscription_seats: org.subscription_seats,
        subscription_active: org.subscription_active,
        ff_public_share: org.ff_public_share,
      }))
      setOrganizations(organizations)

      const groups = [
        {
          name: intl.formatMessage({
            id: "organizations",
          }),
          organizations,
        },
      ]

      setGroups(groups)

      const storedOrg = JSON.parse(
        window.localStorage.getItem("selectedOrganization") || "{}"
      )
      const foundOrg = organizations.find(
        (org: { id: any }) => org.id === storedOrg.id
      )

      let orgToSelect = foundOrg

      if (!orgToSelect) {
        orgToSelect = {
          id: groups[0]?.organizations[0].id,
          name: groups[0]?.organizations[0].name,
          subscription_plan: groups[0]?.organizations[0].subscription_plan,
          subscription_seats: groups[0]?.organizations[0].subscription_seats,
          subscription_billing_cycle:
            groups[0]?.organizations[0].subscription_billing_cycle,
          subscription_active: groups[0]?.organizations[0].subscription_active,
          ff_public_share: groups[0]?.organizations[0].ff_public_share,
        }
      }

      // Check if the selected organization has changed before updating
      if (selectedOrganization?.id !== orgToSelect.id || force) {
        handleSelectOrganization(orgToSelect, force)
      }
    } else {
      console.error(data)
      setLoading(false)
    }
  }

  React.useEffect(() => {
    const fetchOrganizations = async () => {
      if (
        session?.accessToken &&
        organizations.length === 0 &&
        organizations.length === 0
      ) {
        fetchOrganizationsFromApi()
      }
    }

    fetchOrganizations()
  }, [session])

  React.useEffect(() => {
    if (reloadOrganization) {
      fetchOrganizationsFromApi(true)
      setReloadOrganization(false)
    }
  }, [reloadOrganization])

  if (loading) {
    return <Skeleton className="h-8 w-[200px]" />
  }

  if (!selectedOrganization) {
    return null
  }

  const handleSelect = (organization: Organization) => {
    handleSelectOrganization(organization)
    window.localStorage.setItem(
      "selectedOrganization",
      JSON.stringify(organization)
    )
    setOpen(false)
  }

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)
    const response = await fetch(`/api/organizations`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        name: inputName,
        email: inputEmail,
        openaiKey: inputOpenaiKey,
        storageLimit: inputStorageLimit,
      }),
    })
    const data = await response.json()
    if (response.ok) {
      const organization = data.data
      handleSelect({
        name: organization.name,
        id: organization.id,
        subscription_plan: organization.subscription_plan,
        subscription_billing_cycle: organization.subscription_billing_cycle,
        subscription_seats: organization.subscription_seats,
        subscription_active: organization.subscription_active,
        ff_public_share: organization.ff_public_share,
      })
      setShowNewOrganizationDialog(false)
      toast({
        title: intl.formatMessage({ id: "organizationCreated" }),
        description: intl.formatMessage({
          id: "organizationCreatedDescription",
        }),
      })
      setReloadOrganization(!reloadOrganization)
    } else {
      toast({
        title: intl.formatMessage({ id: "errorTitleCreateOrganization" }),
        description: intl.formatMessage({
          id: "errorDescriptionCreateOrganization",
        }),
        variant: "destructive",
      })
    }
    setIsSubmitting(false)
    // clear form
    setInputName("")
    setInputEmail("")
    setInputOpenaiKey("")
    setInputStorageLimit(1)
  }

  return (
    <>
      {organizations && organizations.length === 1 ? (
        <>
          <div className="justify-left flex w-auto pe-6">
            <Avatar className="mr-4 h-5 w-5">
              <AvatarImage
                src={`https://ui-avatars.com/api/?background=random&name=${selectedOrganization.name}`}
                alt={selectedOrganization.name}
              />
              <AvatarFallback>SC</AvatarFallback>
            </Avatar>
            {selectedOrganization.name}
          </div>
          <Separator orientation="vertical" />
        </>
      ) : (
        <Dialog
          open={showNewOrganizationDialog}
          onOpenChange={setShowNewOrganizationDialog}
        >
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                role="combobox"
                aria-expanded={open}
                aria-label={intl.formatMessage({ id: "selectOrganization" })}
                className={cn("w-[200px] justify-between", className)}
              >
                <Avatar className="mr-2 h-5 w-5">
                  <AvatarImage
                    src={`https://ui-avatars.com/api/?background=random&name=${selectedOrganization.name}`}
                    alt={selectedOrganization.name}
                  />
                  <AvatarFallback>SC</AvatarFallback>
                </Avatar>
                {selectedOrganization.name}
                <ChevronsUpDown className="ml-auto h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-[200px] p-0">
              <Command>
                <CommandList>
                  <CommandInput
                    placeholder={intl.formatMessage({
                      id: "searchOrganizations",
                    })}
                  />
                  <CommandEmpty>
                    <FormattedMessage id="noOrganizationFound" />
                  </CommandEmpty>
                  {groups.map((group) => (
                    <CommandGroup key={group.name} heading={group.name}>
                      {group.organizations.map((organization) => (
                        <CommandItem
                          key={organization.id}
                          onSelect={() =>
                            handleSelect({
                              name: organization.name,
                              id: organization.id,
                              subscription_plan: organization.subscription_plan,
                              subscription_seats:
                                organization.subscription_seats,
                              subscription_billing_cycle:
                                organization.subscription_billing_cycle,
                              subscription_active:
                                organization.subscription_active,
                              ff_public_share: organization.ff_public_share,
                            })
                          }
                          className="text-sm"
                        >
                          {/* <Avatar className="mr-2 h-5 w-5">
                            <AvatarImage
                              src={`https://ui-avatars.com/api/?background=random&name=${organization.name}`}
                              alt={organization.name}
                            />
                            <AvatarFallback>SC</AvatarFallback>
                          </Avatar> */}
                          {organization.name}
                          <Check
                            className={cn(
                              "ml-auto h-4 w-4",
                              selectedOrganization.id === organization.id
                                ? "opacity-100"
                                : "opacity-0"
                            )}
                          />
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  ))}
                </CommandList>
                <CommandSeparator />
                {isAppOwner() && (
                  <CommandList>
                    <CommandGroup>
                      <DialogTrigger asChild>
                        <CommandItem
                          onSelect={() => {
                            setOpen(false)
                            setShowNewOrganizationDialog(true)
                          }}
                        >
                          <PlusCircle className="mr-2 h-5 w-5" />
                          <FormattedMessage id="createOrganization" />
                        </CommandItem>
                      </DialogTrigger>
                    </CommandGroup>
                  </CommandList>
                )}
              </Command>
            </PopoverContent>
          </Popover>
          <DialogContent>
            <form onSubmit={(e) => handleSubmit(e)}>
              <DialogHeader>
                <DialogTitle>
                  <FormattedMessage id="createOrganization" />
                </DialogTitle>
                <DialogDescription>
                  <FormattedMessage id="addNewOrganizationDescription" />
                </DialogDescription>
              </DialogHeader>
              <div>
                <div className="space-y-4 py-2 pb-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">
                      *<FormattedMessage id="organizationName" />
                    </Label>
                    <Input
                      id="name"
                      value={inputName}
                      onChange={(e) => setInputName(e.target.value)}
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      *<FormattedMessage id="email" />
                    </Label>
                    <Input
                      id="email"
                      value={inputEmail}
                      onChange={(e) => setInputEmail(e.target.value)}
                      required
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="openaiKey">
                      <FormattedMessage id="openaiKey" />
                    </Label>
                    <Input
                      type="password"
                      id="openaiKey"
                      value={inputOpenaiKey}
                      onChange={(e) => setInputOpenaiKey(e.target.value)}
                      autoComplete="off"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="storageLimit">
                      <FormattedMessage id="storageLimitGB" />
                    </Label>
                    <Input
                      id="storageLimit"
                      value={inputStorageLimit}
                      onChange={(e) =>
                        setInputStorageLimit(Number(e.target.value))
                      }
                      type="number"
                      min={1}
                      max={100}
                      required
                    />
                  </div>
                </div>
              </div>
              <div className="flex items-center justify-start space-x-4 text-sm font-semibold">
                *<FormattedMessage id="requiredFields" />
              </div>
              {isAppOwner() && (
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => setShowNewOrganizationDialog(false)}
                  >
                    <FormattedMessage id="cancel" />
                  </Button>
                  <Button disabled={isSubmitting} type="submit">
                    <FormattedMessage id="continue" />
                  </Button>
                </DialogFooter>
              )}
            </form>
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
